import { create } from 'zustand'
import type { GeneratePreset } from '@/lib/preset-types'
import {
  openDb,
  getAllPresets,
  putPreset,
  deletePreset as idbDeletePreset,
  putThumbnail,
  deleteThumbnail,
} from '@/lib/presetStore'

interface PresetsStore {
  presets: GeneratePreset[]
  loading: boolean
  init: () => Promise<void>
  savePreset: (
    data: Omit<GeneratePreset, 'id' | 'createdAt'>,
    thumbnailBlob?: Blob | null,
  ) => Promise<string>
  updatePreset: (id: string, name: string) => Promise<void>
  deletePreset: (id: string) => Promise<void>
}

export const usePresetsStore = create<PresetsStore>()((set, get) => ({
  presets: [],
  loading: true,

  init: async () => {
    const db = await openDb()
    const all = await getAllPresets(db)
    db.close()
    // Sort newest first (descending createdAt)
    all.sort((a, b) => b.createdAt - a.createdAt)
    set({ presets: all, loading: false })
  },

  savePreset: async (data, thumbnailBlob) => {
    const id = crypto.randomUUID()
    const preset = { ...data, id, createdAt: Date.now() } as GeneratePreset
    const db = await openDb()
    await putPreset(db, preset)
    if (thumbnailBlob) {
      await putThumbnail(db, id, thumbnailBlob)
    }
    db.close()
    set((s) => ({ presets: [preset, ...s.presets] }))
    return id
  },

  updatePreset: async (id, name) => {
    const preset = get().presets.find((p) => p.id === id)
    if (!preset) return
    const updated = { ...preset, name }
    const db = await openDb()
    await putPreset(db, updated)
    db.close()
    set((s) => ({ presets: s.presets.map((p) => (p.id === id ? updated : p)) }))
  },

  deletePreset: async (id) => {
    const db = await openDb()
    await idbDeletePreset(db, id)
    await deleteThumbnail(db, id)
    db.close()
    set((s) => ({ presets: s.presets.filter((p) => p.id !== id) }))
  },
}))
